import { MAX_EVENT_PRICE_THB } from './event-flow-constants';
import type { Database } from './database.types';

export { MAX_EVENT_PRICE_THB, PAYMENT_HOLD_MINUTES } from './event-flow-constants';

export type EventRegistration = Database['public']['Tables']['event_registrations']['Row'];
export type EventPayment = Database['public']['Tables']['event_payments']['Row'];
export type EventPaymentMethod = Database['public']['Enums']['event_payment_method'];
export type EventRegistrationStatus = Database['public']['Enums']['event_registration_status'];

export type OptimisticRegistrationState = Readonly<{
  status: 'idle' | 'holding' | 'confirmed' | 'failed';
  registration: EventRegistration | null;
  error: string | null;
}>;

export type RegistrationStatusCopy = Readonly<{
  actionLabel: string;
  detail: string | null;
  title: string;
}>;

export type OptimisticRegistrationAction =
  | { type: 'hold'; registration?: EventRegistration | null }
  | { type: 'confirm'; registration: EventRegistration }
  | { type: 'fail'; error: string; registration?: EventRegistration | null }
  | { type: 'reset'; registration?: EventRegistration | null };

export function isPaidEvent(event: {
  payment_methods: readonly unknown[];
  registration_price_thb: number;
}): boolean {
  return event.registration_price_thb > 0 && event.payment_methods.length > 0;
}

export function requiresRegistrationHold(
  event: {
    capacity: number | null;
    payment_methods: readonly unknown[];
    registration_price_thb: number;
  },
  options: { hasRegistrationForm?: boolean; paymentBypassed?: boolean } = {}
): boolean {
  return (
    event.capacity != null ||
    (isPaidEvent(event) && !options.paymentBypassed) ||
    Boolean(options.hasRegistrationForm)
  );
}

export function isValidEventPriceThb(amountThb: number): boolean {
  return Number.isInteger(amountThb) && amountThb >= 0 && amountThb <= MAX_EVENT_PRICE_THB;
}

export function formatEventPrice(amountThb: number): string {
  return new Intl.NumberFormat(undefined, {
    currency: 'THB',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    style: 'currency',
  }).format(amountThb);
}

export function isRegistrationHoldActive(
  registration: EventRegistration | null,
  now: Date
): boolean {
  return (
    registration?.status === 'pending_payment' &&
    registration.payment_hold_expires_at != null &&
    new Date(registration.payment_hold_expires_at) > now
  );
}

export function isRegistrationActive(registration: EventRegistration | null, now: Date): boolean {
  return registration?.status === 'confirmed' || isRegistrationHoldActive(registration, now);
}

export function registrationStatusCopy(
  registration: EventRegistration | null,
  now: Date
): RegistrationStatusCopy {
  if (!registration) {
    return {
      actionLabel: 'Register',
      detail: null,
      title: 'Not registered',
    };
  }

  if (registration.status === 'confirmed') {
    return {
      actionLabel: 'Registered',
      detail: 'Your registration is confirmed.',
      title: 'You are registered',
    };
  }

  if (isRegistrationHoldActive(registration, now)) {
    return {
      actionLabel: 'Complete registration',
      detail: 'Complete registration before the hold expires.',
      title: 'Spot held',
    };
  }

  if (registration.status === 'pending_payment') {
    return {
      actionLabel: 'Try again',
      detail: 'Your previous hold expired. Start again to register.',
      title: 'Hold expired',
    };
  }

  if (registration.status === 'cancelled') {
    return {
      actionLabel: 'Register again',
      detail: 'Your previous registration was cancelled.',
      title: 'Registration cancelled',
    };
  }

  if (registration.status === 'expired') {
    return {
      actionLabel: 'Try again',
      detail: 'Your previous hold expired. Start again to register.',
      title: 'Hold expired',
    };
  }

  if (registration.status === 'payment_failed') {
    return {
      actionLabel: 'Try again',
      detail: 'The previous payment attempt failed. Start again to register.',
      title: 'Payment failed',
    };
  }

  return {
    actionLabel: 'Try again',
    detail: 'Start again to finish registration.',
    title: 'Registration incomplete',
  };
}

export function registrationHoldRemainingMs(
  registration: EventRegistration | null,
  now: Date
): number {
  if (!isRegistrationHoldActive(registration, now) || !registration?.payment_hold_expires_at) {
    return 0;
  }

  return Math.max(0, new Date(registration.payment_hold_expires_at).getTime() - now.getTime());
}

export function formatRegistrationHoldCountdown(remainingMs: number): string {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export function optimisticRegistrationReducer(
  state: OptimisticRegistrationState,
  action: OptimisticRegistrationAction
): OptimisticRegistrationState {
  switch (action.type) {
    case 'hold':
      return {
        error: null,
        registration: action.registration ?? state.registration,
        status: 'holding',
      };
    case 'confirm':
      return {
        error: null,
        registration: action.registration,
        status: 'confirmed',
      };
    case 'fail':
      return {
        error: action.error,
        registration: action.registration ?? state.registration,
        status: 'failed',
      };
    case 'reset':
      return {
        error: null,
        registration: action.registration ?? null,
        status: action.registration?.status === 'confirmed' ? 'confirmed' : 'idle',
      };
    default:
      return state;
  }
}
